import {
  FEATURED_PROJECT_FETCH_URLS_API_URL,
  FEATURED_PROJECT_URL_PREFIX,
} from '@utils/constants';
import { ACTIONS } from '@components/Contexts/ProfileContext';
import { useErrorMonitor } from '@wix/fe-essentials-standalone';
import { useEffect } from 'react';

const isExternalPortfolioUrl = (url: string): boolean => {
  return !!url && !url.startsWith(FEATURED_PROJECT_URL_PREFIX);
};

const prefetchPortfolioSite = (url: string) => {
  if (typeof window !== 'undefined' && isExternalPortfolioUrl(url)) {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'document';
    document.head.appendChild(link);
  }
};
const setFallbackUrls = (dispatch: (action: any) => void, ids: string[]) => {
  ids.forEach((id) => {
    dispatch({
      type: ACTIONS.UPDATE_PORTFOLIO_URL,
      metaSiteId: id,
      url: `${FEATURED_PROJECT_URL_PREFIX}/${id}`,
    });
  });
};

const usePortfolioUrlPreloading = (
  baseURL: string,
  metaSiteIds: string[],
  dispatch: (action: any) => void,
  existingMappings: Record<string, string | null> = {},
) => {
  const errorMonitor = useErrorMonitor();

  useEffect(() => {
    const missingIds = metaSiteIds.filter((id) => !(id in existingMappings));
    if (metaSiteIds.length === 0 || missingIds.length === 0) {
      return;
    }

    dispatch({
      type: ACTIONS.SET_PORTFOLIO_LOADING,
      isLoading: true,
    });

    try {
      const url = new URL(`${baseURL}/${FEATURED_PROJECT_FETCH_URLS_API_URL}`);
      url.searchParams.set('metaSiteIds', JSON.stringify(missingIds));

      const eventSource = new EventSource(url.toString());
      const receivedIds = new Set<string>();

      eventSource.onmessage = (event) => {
        try {
          if (!event.data || event.data.trim() === '') {
            // Empty event data
            return;
          }

          const { metaSiteId, url } = JSON.parse(event.data);

          const finalUrl =
            url === 'null' || url === null
              ? `${FEATURED_PROJECT_URL_PREFIX}/${metaSiteId}`
              : url;

          prefetchPortfolioSite(finalUrl);

          receivedIds.add(metaSiteId);

          dispatch({
            type: ACTIONS.UPDATE_PORTFOLIO_URL,
            metaSiteId,
            url: finalUrl,
          });
        } catch (parseError) {
          errorMonitor.captureException(parseError as Error);
        }
      };
    } catch (error) {
      errorMonitor.captureException(error as Error);
      setFallbackUrls(dispatch, missingIds);
    } finally {
      dispatch({
        type: ACTIONS.SET_PORTFOLIO_LOADING,
        isLoading: false,
      });
    }
  }, [metaSiteIds]);
};

export default usePortfolioUrlPreloading;
